import { useEffect, useState } from "react";

const clamp = (n, a, b) => Math.max(a, Math.min(b, n));

// Five-range readiness bands. Muted, financial-services palette — not neon, not gamified.
export const READINESS_BANDS = [
  { min: 0, max: 39, label: "Needs Attention", color: "#B85E5B" },
  { min: 40, max: 59, label: "Building the Foundation", color: "#D98E42" },
  { min: 60, max: 74, label: "Developing Well", color: "#C7A94A" },
  { min: 75, max: 89, label: "Strong Position", color: "#6FA98C" },
  { min: 90, max: 100, label: "Well Planned", color: "#2E8A78" },
];

export function readinessBandFor(score) {
  const s = clamp(Math.round(score || 0), 0, 100);
  return READINESS_BANDS.find((b) => s >= b.min && s <= b.max) || READINESS_BANDS[0];
}

// Segmented semicircle gauge inspired by traditional credit-score meters,
// but restrained: no glow, no needle animation flair, no "poor/excellent" wording.
export const ScoreGauge = ({ score = 0, max = 100 }) => {
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    const target = clamp(score, 0, max);
    let raf;
    const start = performance.now();
    const dur = 1000;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / dur);
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplay(Math.round(target * eased));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [score, max]);

  const band = readinessBandFor(display);

  // Geometry
  const width = 400;
  const height = 260;
  const cx = width / 2;
  const cy = height - 70;
  const r = 155;
  const strokeW = 22;
  const gap = 0.008; // small gap between segments

  const pointAt = (f) => {
    const a = Math.PI * (1 - f);
    return [cx + r * Math.cos(a), cy - r * Math.sin(a)];
  };
  const arcPath = (f1, f2) => {
    const [x1, y1] = pointAt(f1);
    const [x2, y2] = pointAt(f2);
    const large = f2 - f1 > 0.5 ? 1 : 0;
    return `M ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2}`;
  };

  // Segment ranges normalized 0..1 aligned to 0/40/60/75/90/100
  const SEGMENTS = [
    { start: 0.00, end: 0.40, color: "#B85E5B" },
    { start: 0.40, end: 0.60, color: "#D98E42" },
    { start: 0.60, end: 0.75, color: "#C7A94A" },
    { start: 0.75, end: 0.90, color: "#6FA98C" },
    { start: 0.90, end: 1.00, color: "#2E8A78" },
  ];
  const scoreFraction = clamp(display / max, 0, 1);
  const [markerX, markerY] = pointAt(scoreFraction);

  const TICKS = [0, 40, 60, 75, 90, 100];

  return (
    <div className="flex flex-col items-center w-full" data-testid="score-gauge">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        width="100%"
        style={{ maxWidth: 460 }}
        role="img"
        aria-label={`Score ${display} of ${max}. ${band.label}.`}
      >
        {/* Background segments (dimmed) */}
        {SEGMENTS.map((seg, i) => (
          <path
            key={`bg-${i}`}
            d={arcPath(seg.start + gap / 2, seg.end - gap / 2)}
            stroke={seg.color}
            strokeWidth={strokeW}
            fill="none"
            strokeLinecap="butt"
            opacity="0.28"
            data-testid={`gauge-segment-${i}`}
          />
        ))}
        {/* Active segment highlighted */}
        {SEGMENTS.map((seg, i) => {
          const isActive =
            scoreFraction >= seg.start &&
            (scoreFraction < seg.end || (seg.end === 1.0 && scoreFraction >= 0.999));
          if (!isActive) return null;
          return (
            <path
              key={`fg-${i}`}
              d={arcPath(seg.start + gap / 2, seg.end - gap / 2)}
              stroke={seg.color}
              strokeWidth={strokeW}
              fill="none"
              strokeLinecap="butt"
              opacity="1"
              data-testid={`gauge-segment-active`}
            />
          );
        })}
        {/* Tick labels */}
        {TICKS.map((t) => {
          const angle = Math.PI * (1 - t / 100);
          const outer = r + 18;
          const lx = cx + outer * Math.cos(angle);
          const ly = cy - outer * Math.sin(angle);
          return (
            <text
              key={`tick-${t}`}
              x={lx}
              y={ly}
              textAnchor="middle"
              dominantBaseline="middle"
              style={{
                fontSize: 11,
                fill: "#3a4d70",
                fontFamily: "'IBM Plex Sans', sans-serif",
                fontWeight: 500,
              }}
            >
              {t}
            </text>
          );
        })}
        {/* Position marker */}
        <circle cx={markerX} cy={markerY} r={11} fill="#ffffff" stroke="#1E1968" strokeWidth={3} />
        <circle cx={markerX} cy={markerY} r={4.5} fill="#1E1968" />
      </svg>

      {/* Score + band label */}
      <div className="text-center -mt-4">
        <div className="flex items-baseline justify-center gap-2" style={{ lineHeight: 1 }}>
          <span
            data-testid="score-value"
            style={{
              fontFamily: "'Playfair Display', serif",
              fontWeight: 600,
              color: "#1E1968",
              fontSize: "clamp(2.75rem, 6vw, 4rem)",
            }}
          >
            {display}
          </span>
          <span
            data-testid="score-denominator"
            style={{
              fontFamily: "'Playfair Display', serif",
              fontWeight: 500,
              color: "#173A6A",
              fontSize: "clamp(1.35rem, 3vw, 1.85rem)",
            }}
          >
            / {max}
          </span>
        </div>
        <div
          className="mt-1"
          style={{
            fontFamily: "'IBM Plex Sans', sans-serif",
            fontSize: "0.72rem",
            fontWeight: 500,
            color: "#3a4d70",
            letterSpacing: "0.18em",
            textTransform: "uppercase",
          }}
        >
          Overall Score
        </div>
        <div
          className="mt-3 inline-flex items-center gap-2"
          data-testid="score-band-label"
        >
          <span
            aria-hidden
            style={{
              display: "inline-block",
              width: 10,
              height: 10,
              background: band.color,
              borderRadius: 0,
            }}
          />
          <span
            style={{
              fontFamily: "'Playfair Display', serif",
              color: "#1E1968",
              fontSize: "1.15rem",
              fontWeight: 600,
              letterSpacing: "-0.005em",
            }}
          >
            {band.label}
          </span>
        </div>
      </div>
    </div>
  );
};

export default ScoreGauge;
